/**
 * Champion/Class system — LoL-inspired abilities in a voxel world.
 * Each class has 4 abilities (Q/W/E/R) with cooldowns and mana costs.
 */

export enum ChampionClass {
  WARRIOR = "warrior",
  MAGE = "mage",
  RANGER = "ranger",
  ASSASSIN = "assassin",
  GUARDIAN = "guardian",
}

export enum AbilityType {
  PROJECTILE = "projectile", // fires in look direction
  AREA = "area", // AoE around caster or target
  BUFF = "buff", // self or ally buff
  DASH = "dash", // movement ability
  SUMMON = "summon", // creates entity
}

export enum AbilitySlot {
  Q = 0,
  W = 1,
  E = 2,
  R = 3, // ultimate
}

export type AbilityDef = {
  id: string;
  name: string;
  description: string;
  slot: AbilitySlot;
  type: AbilityType;
  cooldown: number; // seconds
  manaCost: number;
  damage: number;
  range: number; // blocks
  radius: number; // AoE radius (0 for single target)
  duration: number; // buff/effect duration seconds (0 for instant)
  color: string; // visual color hex
};

export type ClassDef = {
  id: ChampionClass;
  name: string;
  title: string;
  /** Theme color — also the outfit color every player sees (synced). */
  color: string;
  maxHealth: number;
  maxMana: number;
  manaRegen: number; // per second
  moveSpeedBonus: number; // multiplier (1.0 = normal)
  abilities: [AbilityDef, AbilityDef, AbilityDef, AbilityDef];
};

/** Basic punch damage — abilities are the strong hits, not the fist. */
export const BASE_MELEE_DAMAGE = 5;

// --- CLASS DEFINITIONS ---

export const CLASS_DEFS: Record<ChampionClass, ClassDef> = {
  [ChampionClass.WARRIOR]: {
    id: ChampionClass.WARRIOR,
    name: "Guerreiro",
    title: "A Vanguarda",
    color: "#ff4444",
    maxHealth: 120,
    maxMana: 60,
    manaRegen: 2,
    moveSpeedBonus: 1.0,
    abilities: [
      {
        id: "warrior_q",
        name: "Golpe Amplo",
        description: "Golpeia em arco, causando dano aos inimigos próximos",
        slot: AbilitySlot.Q,
        type: AbilityType.AREA,
        cooldown: 4,
        manaCost: 10,
        damage: 25,
        range: 3,
        radius: 2.5,
        duration: 0,
        color: "#ff4444",
      },
      {
        id: "warrior_w",
        name: "Muralha de Escudos",
        description: "Ergue o escudo, reduzindo o dano recebido em 50%",
        slot: AbilitySlot.W,
        type: AbilityType.BUFF,
        cooldown: 12,
        manaCost: 20,
        damage: 0,
        range: 0,
        radius: 0,
        duration: 4,
        color: "#ffaa00",
      },
      {
        id: "warrior_e",
        name: "Investida",
        description: "Avança para frente, atordoando o primeiro inimigo atingido",
        slot: AbilitySlot.E,
        type: AbilityType.DASH,
        cooldown: 10,
        manaCost: 15,
        damage: 15,
        range: 8,
        radius: 0,
        duration: 1.5,
        color: "#ff8800",
      },
      {
        id: "warrior_r",
        name: "Fúria Berserker",
        description: "Entra em fúria: +40% de dano e +20% de velocidade por 8s",
        slot: AbilitySlot.R,
        type: AbilityType.BUFF,
        cooldown: 60,
        manaCost: 40,
        damage: 0,
        range: 0,
        radius: 0,
        duration: 8,
        color: "#ff0000",
      },
    ],
  },

  [ChampionClass.MAGE]: {
    id: ChampionClass.MAGE,
    name: "Mago",
    title: "O Arcano",
    color: "#6644ff",
    maxHealth: 80,
    maxMana: 120,
    manaRegen: 5,
    moveSpeedBonus: 0.95,
    abilities: [
      {
        id: "mage_q",
        name: "Bola de Fogo",
        description: "Lança uma bola de fogo que explode ao atingir o alvo",
        slot: AbilitySlot.Q,
        type: AbilityType.PROJECTILE,
        cooldown: 3,
        manaCost: 15,
        damage: 35,
        range: 20,
        radius: 2,
        duration: 0,
        color: "#ff6600",
      },
      {
        id: "mage_w",
        name: "Nova de Gelo",
        description: "Congela os inimigos ao seu redor, reduzindo a velocidade deles",
        slot: AbilitySlot.W,
        type: AbilityType.AREA,
        cooldown: 8,
        manaCost: 25,
        damage: 15,
        range: 0,
        radius: 4,
        duration: 3,
        color: "#66ccff",
      },
      {
        id: "mage_e",
        name: "Deslocamento",
        description: "Teleporta-se por uma curta distância na direção em que está olhando",
        slot: AbilitySlot.E,
        type: AbilityType.DASH,
        cooldown: 14,
        manaCost: 30,
        damage: 0,
        range: 10,
        radius: 0,
        duration: 0,
        color: "#aa44ff",
      },
      {
        id: "mage_r",
        name: "Tempestade de Meteoros",
        description: "Chove meteoros em uma grande área por 5s",
        slot: AbilitySlot.R,
        type: AbilityType.AREA,
        cooldown: 90,
        manaCost: 60,
        damage: 80,
        range: 15,
        radius: 6,
        duration: 5,
        color: "#ff2200",
      },
    ],
  },

  [ChampionClass.RANGER]: {
    id: ChampionClass.RANGER,
    name: "Arqueiro",
    title: "O Atirador de Elite",
    color: "#44ff44",
    maxHealth: 90,
    maxMana: 80,
    manaRegen: 3,
    moveSpeedBonus: 1.1,
    abilities: [
      {
        id: "ranger_q",
        name: "Flecha Perfurante",
        description: "Dispara uma flecha que atravessa os inimigos",
        slot: AbilitySlot.Q,
        type: AbilityType.PROJECTILE,
        cooldown: 2,
        manaCost: 8,
        damage: 20,
        range: 30,
        radius: 0,
        duration: 0,
        color: "#44ff44",
      },
      {
        id: "ranger_w",
        name: "Armadilha",
        description: "Coloca uma armadilha que imobiliza inimigos por 2s",
        slot: AbilitySlot.W,
        type: AbilityType.SUMMON,
        cooldown: 15,
        manaCost: 20,
        damage: 10,
        range: 5,
        radius: 1.5,
        duration: 2,
        color: "#88cc44",
      },
      {
        id: "ranger_e",
        name: "Rolamento",
        description: "Rola para o lado, ganhando invulnerabilidade breve",
        slot: AbilitySlot.E,
        type: AbilityType.DASH,
        cooldown: 8,
        manaCost: 12,
        damage: 0,
        range: 5,
        radius: 0,
        duration: 0.5,
        color: "#aaffaa",
      },
      {
        id: "ranger_r",
        name: "Chuva de Flechas",
        description: "Bombardeia a área alvo com flechas por 3s",
        slot: AbilitySlot.R,
        type: AbilityType.AREA,
        cooldown: 70,
        manaCost: 50,
        damage: 60,
        range: 25,
        radius: 5,
        duration: 3,
        color: "#22ff22",
      },
    ],
  },

  [ChampionClass.ASSASSIN]: {
    id: ChampionClass.ASSASSIN,
    name: "Assassino",
    title: "A Sombra",
    color: "#aa44cc",
    maxHealth: 85,
    maxMana: 70,
    manaRegen: 3,
    moveSpeedBonus: 1.15,
    abilities: [
      {
        id: "assassin_q",
        name: "Golpe das Sombras",
        description: "Ataque rápido que causa dano bônus pelas costas",
        slot: AbilitySlot.Q,
        type: AbilityType.AREA,
        cooldown: 3,
        manaCost: 10,
        damage: 30,
        range: 2,
        radius: 1,
        duration: 0,
        color: "#9944cc",
      },
      {
        id: "assassin_w",
        name: "Desaparecer",
        description: "Torna-se invisível por 3s. O próximo ataque é crítico",
        slot: AbilitySlot.W,
        type: AbilityType.BUFF,
        cooldown: 18,
        manaCost: 25,
        damage: 0,
        range: 0,
        radius: 0,
        duration: 3,
        color: "#6622aa",
      },
      {
        id: "assassin_e",
        name: "Passo das Sombras",
        description: "Teleporta-se para trás do inimigo alvo",
        slot: AbilitySlot.E,
        type: AbilityType.DASH,
        cooldown: 12,
        manaCost: 20,
        damage: 0,
        range: 12,
        radius: 0,
        duration: 0,
        color: "#330066",
      },
      {
        id: "assassin_r",
        name: "Marca da Morte",
        description: "Marca o inimigo: após 3s, causa o dano acumulado",
        slot: AbilitySlot.R,
        type: AbilityType.PROJECTILE,
        cooldown: 80,
        manaCost: 40,
        damage: 100,
        range: 5,
        radius: 0,
        duration: 3,
        color: "#ff00ff",
      },
    ],
  },

  [ChampionClass.GUARDIAN]: {
    id: ChampionClass.GUARDIAN,
    name: "Guardião",
    title: "O Protetor",
    color: "#ffcc00",
    maxHealth: 150,
    maxMana: 80,
    manaRegen: 3,
    moveSpeedBonus: 0.9,
    abilities: [
      {
        id: "guardian_q",
        name: "Golpe Sagrado",
        description: "Golpe de martelo que arremessa os inimigos para trás",
        slot: AbilitySlot.Q,
        type: AbilityType.AREA,
        cooldown: 5,
        manaCost: 12,
        damage: 18,
        range: 3,
        radius: 2,
        duration: 0,
        color: "#ffcc00",
      },
      {
        id: "guardian_w",
        name: "Escudo Sagrado",
        description: "Protege um aliado ou a si mesmo, absorvendo 40 de dano",
        slot: AbilitySlot.W,
        type: AbilityType.BUFF,
        cooldown: 10,
        manaCost: 25,
        damage: 0,
        range: 8,
        radius: 0,
        duration: 6,
        color: "#ffffaa",
      },
      {
        id: "guardian_e",
        name: "Provocar",
        description: "Força os inimigos próximos a atacarem você por 2s",
        slot: AbilitySlot.E,
        type: AbilityType.AREA,
        cooldown: 16,
        manaCost: 20,
        damage: 0,
        range: 0,
        radius: 4,
        duration: 2,
        color: "#ff8844",
      },
      {
        id: "guardian_r",
        name: "Fortaleza Divina",
        description: "Cria uma zona de barreira: aliados dentro recebem -60% de dano",
        slot: AbilitySlot.R,
        type: AbilityType.AREA,
        cooldown: 100,
        manaCost: 50,
        damage: 0,
        range: 0,
        radius: 6,
        duration: 8,
        color: "#ffff00",
      },
    ],
  },
};
